import { Box, Button, Card, CardActions, CardContent, CardMedia, Chip, Typography } from "@mui/material";
import { useState } from "react";
import type {
  AssistantAction,
  AssistantActionStatus,
  AssistantMappingCard,
  AssistantMappingChoices,
} from "../../../../api/types/assistantTypes";
import MappingCardTable from "./MappingCardTable";
import TaskCardContent from "./TaskCardContent";

interface Props {
  action: AssistantAction;
  disabled: boolean;
  onDecision: (action: AssistantAction, approve: boolean, mappingChoices?: AssistantMappingChoices) => void;
  onShowImage: (imageUrl: string) => void;
}

const statusChips: Record<
  Exclude<AssistantActionStatus, "Pending">,
  { label: string; color: "success" | "error" | "default" }
> = {
  Approved: { label: "Approved", color: "default" },
  Running: { label: "Running", color: "default" },
  Succeeded: { label: "Done", color: "success" },
  Failed: { label: "Failed", color: "error" },
  Rejected: { label: "Rejected", color: "default" },
  Expired: { label: "Expired", color: "default" },
  NotRun: { label: "Not run", color: "default" },
};

const getProposedChoices = (mapping: AssistantMappingCard): AssistantMappingChoices =>
  Object.fromEntries(mapping.targets.map((target) => [target.key, target.selectedSource]));

const getApproveLabel = ({ mapping, taskCard }: AssistantAction): string => {
  if (mapping) {
    return "Use this mapping";
  }
  if (taskCard) {
    return taskCard.isEdit ? "Save changes" : "Create task";
  }
  return "Approve";
};

const ActionCard = ({ action, disabled, onDecision, onShowImage }: Props) => {
  const { status, imageUrl, mapping, taskCard } = action;
  const [mappingChoices, setMappingChoices] = useState(() => (mapping ? getProposedChoices(mapping) : undefined));

  const handleMappingChange = (targetKey: string, sourceKey: string | null) =>
    setMappingChoices((prev) => ({ ...prev, [targetKey]: sourceKey }));

  return (
    <Card variant="outlined">
      {imageUrl && (
        <CardMedia
          sx={{ height: 160, cursor: "pointer" }}
          image={imageUrl}
          title={action.title}
          onClick={() => onShowImage(imageUrl)}
        />
      )}
      <CardContent>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="subtitle2">{action.title}</Typography>
          {status !== "Pending" && <Chip label={statusChips[status].label} color={statusChips[status].color} />}
        </Box>
        {taskCard ? (
          <Box sx={{ mt: 1 }}>
            <TaskCardContent taskCard={taskCard} />
          </Box>
        ) : (
          action.lines.map((line) => (
            <Typography key={line} color="textSecondary" sx={{ wordBreak: "break-word" }}>
              {line}
            </Typography>
          ))
        )}
        {mapping && mappingChoices && (
          <Box sx={{ mt: 1 }}>
            <MappingCardTable
              mapping={mapping}
              choices={mappingChoices}
              disabled={disabled || status !== "Pending"}
              onChange={handleMappingChange}
            />
          </Box>
        )}
      </CardContent>
      {status === "Pending" && (
        <CardActions>
          <Button variant="contained" disabled={disabled} onClick={() => onDecision(action, true, mappingChoices)}>
            {getApproveLabel(action)}
          </Button>
          <Button
            variant="outlined"
            color="secondary"

            disabled={disabled}
            onClick={() => onDecision(action, false)}
          >
            Reject
          </Button>
        </CardActions>
      )}
    </Card>
  );
};

export default ActionCard;
